import { useState, type FormEvent } from "react";
import {
  contactRequestSchema,
  type ContactRequest,
} from "@general-applications/contracts";

import {
  Button,
  Card,
  Field,
  FieldError,
  FieldLabel,
  Grid,
  Heading,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  Textarea,
} from "@generalapplications/ui";
import { ContactHoneypot } from "./ContactHoneypot";
import { ContactStatusNotice } from "./ContactStatusNotice";
import { contactPage } from "@/content/contact";
import { submitContactForm } from "@/app/common/functions/api/contactApi";
import { Reveal } from "@/app/common/components/composite/motion/Reveal";
import {
  RevealGroup,
  RevealItem,
} from "@/app/common/components/composite/motion/RevealGroup";

type FormState = Omit<ContactRequest, "company">;

const initialState: FormState = {
  name: "",
  workEmail: "",
  organization: "",
  role: "",
  interest: "",
  message: "",
};

const textFields: Array<{
  key: keyof Omit<FormState, "message" | "interest">;
  label: string;
  autoComplete: string;
}> = [
  { key: "name", label: contactPage.fields[0], autoComplete: "name" },
  { key: "workEmail", label: contactPage.fields[1], autoComplete: "email" },
  {
    key: "organization",
    label: contactPage.fields[2],
    autoComplete: "organization",
  },
  {
    key: "role",
    label: contactPage.fields[3],
    autoComplete: "organization-title",
  },
];

type Status = "idle" | "submitting" | "success" | "error";

export function ContactFormSection() {
  const [values, setValues] = useState<FormState>(initialState);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});
  const [status, setStatus] = useState<Status>("idle");
  const [statusMessage, setStatusMessage] = useState("");

  function updateField(key: keyof FormState, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const honeypot = new FormData(event.currentTarget).get("company");
    const parsed = contactRequestSchema.safeParse({
      ...values,
      company: typeof honeypot === "string" ? honeypot : "",
    });

    if (!parsed.success) {
      const errors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState;
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      setStatus("idle");
      return;
    }

    setFieldErrors({});
    setStatus("submitting");

    const result = await submitContactForm(parsed.data);

    if (result.ok) {
      setStatus("success");
      setStatusMessage(contactPage.successMessage);
      setValues(initialState);
      return;
    }

    // The server validates with the same schema the client just checked
    // above, so this only fires on schema drift between deployed versions
    // — rare, but worth surfacing as real field errors rather than a
    // generic banner if it ever happens.
    if (result.code === "validation_error") {
      setFieldErrors(result.fieldErrors);
      setStatus("idle");
      return;
    }

    setStatus("error");
    setStatusMessage(
      result.code === "rate_limited"
        ? contactPage.rateLimitMessage
        : contactPage.genericErrorMessage,
    );
  }

  const isSubmitting = status === "submitting";

  return (
    <Grid as="section" columns={{ base: 1, lg: 2 }} gap="xxl" align="start">
      <Reveal>
        <Stack gap="md">
          <Heading level={2} size="xxl">
            {contactPage.formIntro}
          </Heading>
          <Text variant="prose">{contactPage.alternateContact}</Text>
        </Stack>
      </Reveal>

      <Card variant="glass" radius="xxl" padding="lg">
        <form onSubmit={handleSubmit} noValidate>
          <RevealGroup>
            <Stack gap="md">
              <ContactHoneypot />

              {textFields.map(({ key, label, autoComplete }) => {
                const error = fieldErrors[key];
                return (
                  <RevealItem key={key}>
                    <Field invalid={Boolean(error)}>
                      <FieldLabel htmlFor={key}>{label}</FieldLabel>
                      <Input
                        id={key}
                        name={key}
                        autoComplete={autoComplete}
                        value={values[key]}
                        onChange={(event) =>
                          updateField(key, event.target.value)
                        }
                        invalid={Boolean(error)}
                        disabled={isSubmitting}
                      />
                      {error ? <FieldError>{error}</FieldError> : null}
                    </Field>
                  </RevealItem>
                );
              })}

              <RevealItem>
                <Field invalid={Boolean(fieldErrors.interest)}>
                  <FieldLabel htmlFor="interest">I am interested in</FieldLabel>
                  <Select
                    value={values.interest}
                    onValueChange={(value) => updateField("interest", value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger
                      id="interest"
                      invalid={Boolean(fieldErrors.interest)}
                      width="full"
                    >
                      <SelectValue placeholder="Choose a focus area" />
                    </SelectTrigger>
                    <SelectContent>
                      {contactPage.interests.map((interest) => (
                        <SelectItem key={interest} value={interest}>
                          {interest}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.interest ? (
                    <FieldError>{fieldErrors.interest}</FieldError>
                  ) : null}
                </Field>
              </RevealItem>

              <RevealItem>
                <Field invalid={Boolean(fieldErrors.message)}>
                  <FieldLabel htmlFor="message">
                    {contactPage.fields[4]}
                  </FieldLabel>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={values.message}
                    onChange={(event) =>
                      updateField("message", event.target.value)
                    }
                    invalid={Boolean(fieldErrors.message)}
                    disabled={isSubmitting}
                  />
                  {fieldErrors.message ? (
                    <FieldError>{fieldErrors.message}</FieldError>
                  ) : null}
                </Field>
              </RevealItem>

              <RevealItem>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? contactPage.submittingLabel
                    : contactPage.submitLabel}
                </Button>
              </RevealItem>

              {status === "success" || status === "error" ? (
                <RevealItem>
                  <ContactStatusNotice
                    status={status}
                    message={statusMessage}
                  />
                </RevealItem>
              ) : null}
            </Stack>
          </RevealGroup>
        </form>
      </Card>
    </Grid>
  );
}
